import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api";

/**
 * The TanStack Query client for request/response data (design 12.1). The live stream is NOT a query
 * — it has its own store (`state/live-stream.ts`) and client (task 11.3). This client is for the
 * screens' point-in-time reads.
 *
 * Retry policy is tuned to the BFF's failure classes (`lib/api.ts`):
 * - A cold-core `waking` 503 is retried a few times with a backoff bounded by the server's
 *   `Retry-After`, because the control plane is coming up and the call will likely succeed shortly
 *   (NFR-7 AC1). The shell shows "Waking the control plane" meanwhile.
 * - A `service_unavailable` 503 (a hosted IDP/AIS/VCI service starting behind the service-admin
 *   proxy) is retried the same way, but it never raises the core banner (`useColdCore` keys on
 *   `waking` only). Mutations are NOT retried for it.
 * - `unauthorized`, `forbidden`, `not_found` and other request errors are NOT retried: the answer
 *   will not change by asking again.
 * - A `network` or non-waking `server` error is retried once.
 */

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    switch (error.kind) {
      case "waking":
      case "service_unavailable":
        return failureCount < 4;
      case "network":
      case "server":
        return failureCount < 1;
      case "unauthorized":
      case "forbidden":
      case "not_found":
      case "request":
      case "malformed":
        return false;
    }
  }
  // Unknown errors: one retry, then surface.
  return failureCount < 1;
}

function retryDelayMs(attemptIndex: number, error: unknown): number {
  // Honour the server's Retry-After for a waking core when it gave one; otherwise exponential
  // backoff capped at 10s so the UI reconnects quickly once core is warm.
  if (error instanceof ApiError && error.retryAfterSeconds !== null) {
    return Math.min(error.retryAfterSeconds * 1000, 15_000);
  }
  return Math.min(1000 * 2 ** attemptIndex, 10_000);
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        retryDelay: retryDelayMs,
        // Operator telemetry is read often and tolerates short staleness; screens that need fresher
        // data (or live behaviour) override this or use the live stream.
        staleTime: 15_000,
        refetchOnWindowFocus: false,
      },
      mutations: {
        // A mutation (ack an alert, create a rule) is retried only when core is waking.
        retry: (failureCount, error) =>
          error instanceof ApiError && error.kind === "waking" && failureCount < 2,
        retryDelay: retryDelayMs,
      },
    },
  });
}
